import { Flex } from 'antd';
import { useGetRates } from '@/features/agreements/hooks/useRates';
import { EmptyStateCard } from '../../EmptyStateCard';
import { LoadingIndicator } from '@/shared/components/LoadingIndicator';
import { ErrorMessage } from '@/shared/components/ErrorMessage';
import { RateCard } from './RateCard';
import { CreateRateButton } from './CreateRateButton';
import { CreateRateDrawer } from './CreateRateDrawer';
import { useModal } from '@/shared/hooks/useModal';
import { useQueryClient } from '@tanstack/react-query';
import { ratesQueryKeys } from '@/features/agreements/hooks/useRates';

// Exportar para uso en RulesStep
export { CreateRateButton };

const getOperatorLabel = (operator) => {
  const operatorMap = {
    'eq': 'igual a',
    'neq': 'diferente de',
    'gt': 'mayor que',
    'gte': 'mayor o igual que',
    'lt': 'menor que',
    'lte': 'menor o igual que',
    'in': 'incluido en',
    'not_in': 'no incluido en',
    'between': 'entre',
    'contains': 'contiene',
  };
  return operatorMap[operator] || operator;
};

const getOperatorDescription = (operator) => {
  const operatorDescriptions = {
    'and': 'Se deben cumplir todas las condiciones',
    'or': 'Se debe cumplir al menos una condición',
    'not': 'Se niega la condición',
  };
  return operatorDescriptions[operator] || `Operador: ${operator}`;
};

export const PlanRatesTab = ({ agreementId, plan, readOnly = false }) => {
  const queryClient = useQueryClient();
  const ratesQuery = useGetRates({ agreementId, planId: plan.id });
  const editRateDrawer = useModal(null);

  const rates = ratesQuery.data ?? [];
  const hasRates = rates.length > 0;

  const handleRateCreated = () => {
    void queryClient.invalidateQueries({
      queryKey: ratesQueryKeys.list({ agreementId, planId: plan.id }),
    });
  };

  const handleEditRate = (rate) => {
    editRateDrawer.open(rate);
  };

  if (ratesQuery.isLoading) return <LoadingIndicator />;
  if (ratesQuery.isError) return <ErrorMessage error={ratesQuery.error} />;

  if (!hasRates) {
    return (
      <>
        <EmptyStateCard
          title='Este plan aún no tiene tasas configuradas'
          description='Crea una o más tasas para este plan y define las condiciones bajo las cuales deben aplicarse durante una cotización.'
          action={!readOnly ? (
            <CreateRateButton
              agreementId={agreementId}
              planId={plan.id}
              onRateCreated={handleRateCreated}
            />
          ) : null}
        />
        <CreateRateDrawer
          open={editRateDrawer.isOpen}
          onClose={editRateDrawer.close}
          agreementId={agreementId}
          planId={plan.id}
          rate={editRateDrawer.data}
          onSuccess={handleRateCreated}
        />
      </>
    );
  }

  return (
    <>
      <Flex vertical>
        {rates.map((rate) => (
          <RateCard
            key={rate.id}
            rate={rate}
            agreementId={agreementId}
            planId={plan.id}
            getOperatorLabel={getOperatorLabel}
            getOperatorDescription={getOperatorDescription}
            onEdit={handleEditRate}
            readOnly={readOnly}
          />
        ))}
      </Flex>
      <CreateRateDrawer
        open={editRateDrawer.isOpen}
        onClose={editRateDrawer.close}
        agreementId={agreementId}
        planId={plan.id}
        rate={editRateDrawer.data}
        onSuccess={handleRateCreated}
      />
    </>
  );
};
